/**
 * DemosInteractives.jsx — Page hub des 9 démos pédagogiques Code 2 Roues
 *
 * Route : /demos-interactives (public, sans auth)
 * - Mobile/tablette optimisé
 * - Bannière "défi d'un ami" si URL contient ?challenge=xxx&value=yyy
 */
import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Home, Trophy, X } from 'lucide-react';
import { DEMOS } from '../components/demos';
import { FadeUp, Stagger, StaggerItem } from '../components/MotionReveals';
import useSeo from '../hooks/useSeo';

export default function DemosInteractives() {
  useSeo({
    title: '9 démos pédagogiques moto · Code 2 Roues',
    description: 'Comprends en 30 secondes ce qu\'un cours met 30 minutes. 9 démonstrations interactives moto : contre-braquage, tache aveugle, angles morts, effet tunnel, temps de réaction, énergie cinétique, panneaux flash, PVT fatigue, vision sous alcool.',
  });

  const [params, setParams] = useSearchParams();

  // Challenge banner (si lien partagé)
  const challenge = useMemo(() => {
    const slug = params.get('challenge');
    const value = params.get('value');
    const unit = params.get('unit') || 'ms';
    if (!slug || !value) return null;
    const demo = DEMOS[slug];
    if (!demo) return null;
    return { slug, value, unit, demoTitle: demo.title };
  }, [params]);

  const closeChallenge = () => {
    const next = new URLSearchParams(params);
    next.delete('challenge');
    next.delete('value');
    next.delete('unit');
    setParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="demos-interactives-page">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-white/5 bg-black/80 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 lg:px-6 py-2.5 sm:py-3 flex items-center justify-between gap-3">
          <Link to="/" className="inline-flex items-center gap-1.5 text-white/70 hover:text-white transition text-[10.5px] sm:text-[11px] font-black uppercase tracking-widest" data-testid="btn-back-home">
            <Home className="w-4 h-4" /> Accueil
          </Link>
          <div className="text-[9px] sm:text-[10px] font-black uppercase tracking-[0.22em] text-red-400">
            Démos · Code 2 Roues
          </div>
        </div>
      </header>

      {/* Bannière défi */}
      {challenge && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="sticky top-[49px] sm:top-[53px] z-20 border-b border-amber-500/40 bg-gradient-to-r from-amber-600/95 via-orange-500/95 to-red-600/95 backdrop-blur"
          data-testid="challenge-banner"
        >
          <div className="max-w-6xl mx-auto px-3 sm:px-4 lg:px-6 py-2 sm:py-2.5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-black/30 border border-white/40 flex items-center justify-center flex-shrink-0">
                <Trophy className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div className="min-w-0">
                <div className="text-[9.5px] sm:text-[10.5px] font-black uppercase tracking-[0.22em] text-white/80">
                  Défi d&apos;un ami
                </div>
                <div className="text-white text-[12.5px] sm:text-[14px] font-bold truncate">
                  <span className="hidden sm:inline">{challenge.demoTitle} : </span>
                  <strong className="text-black bg-white/90 px-1.5 py-0.5 rounded font-black">{challenge.value} {challenge.unit}</strong>
                  <span className="ml-1.5 text-white/90 font-semibold">à battre !</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <a
                href={`#${challenge.slug}`}
                className="hidden sm:inline-flex items-center gap-1 h-9 px-3 rounded-full bg-white text-red-600 text-[11px] font-black uppercase tracking-widest hover:scale-105 transition-transform"
                data-testid="btn-accept-challenge"
              >
                Relever <ArrowRight className="w-3.5 h-3.5" />
              </a>
              <button
                type="button"
                onClick={closeChallenge}
                className="w-8 h-8 rounded-full bg-black/20 hover:bg-black/40 text-white flex items-center justify-center transition"
                data-testid="btn-close-challenge"
                aria-label="Fermer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}

      <section className="max-w-6xl mx-auto px-3 sm:px-4 lg:px-6 mt-6 sm:mt-10 lg:mt-12 mb-10 sm:mb-16">
        <FadeUp>
          <div className="mb-6 sm:mb-10">
            <div className="inline-flex items-center gap-1.5 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-red-600/15 border border-red-700/30 text-[9px] sm:text-[9.5px] font-black uppercase tracking-[0.22em] text-red-400 mb-2 sm:mb-3">
              <Sparkles className="w-3 h-3" /> 9 démos pédagogiques · uniques en France
            </div>
            <h1 className="font-serif-editorial text-white text-[26px] sm:text-[44px] md:text-[56px] lg:text-[64px] leading-[1.02] font-bold tracking-tight">
              Comprends en 30 secondes<br />
              <span className="text-red-500">ce qu'un cours met 30 minutes.</span>
            </h1>
            <p className="mt-3 sm:mt-4 text-white/70 text-[13px] sm:text-[15px] md:text-[16px] leading-relaxed max-w-3xl">
              9 démonstrations interactives calibrées par des moniteurs ECSR, <strong className="text-white">adaptées à la moto</strong> : le contre-braquage (exclusif moto !), ta tache aveugle, les angles morts, l'effet tunnel, ton temps de réaction, la violence d'un choc à 90 km/h, les panneaux flash, le PVT NASA, et ta vision sous alcool.
            </p>
            <p className="mt-2 text-white/50 text-[11.5px] sm:text-[13.5px]">
              <strong className="text-emerald-400">Gratuit</strong> · <strong className="text-white/80">sans inscription</strong> · <strong className="text-white/80">projetable en salle</strong>
            </p>
          </div>
        </FadeUp>

        <FadeUp>
          <div className="rounded-xl sm:rounded-2xl border border-red-700/30 bg-red-950/15 p-3 sm:p-5 mb-6 sm:mb-10 flex items-start gap-2.5 sm:gap-3">
            <Sparkles size={16} className="text-red-400 shrink-0 mt-0.5 sm:w-[18px] sm:h-[18px]" />
            <div className="text-white/80 text-[12px] sm:text-[14px] leading-relaxed">
              <span className="text-white font-bold">Comment ça marche ?</span> Chaque démo est <strong className="text-red-300">100 % autonome</strong> — lance-la en classe, projette-la, ou laisse un élève la découvrir seul. Aucune installation, chaque test dure moins de 2 minutes. Teste ton score, puis <strong className="text-amber-300">défie tes amis</strong> avec un lien unique.
            </div>
          </div>
        </FadeUp>

        <Stagger className="space-y-5 sm:space-y-7 lg:space-y-8" staggerChildren={0.06}>
          {Object.entries(DEMOS).map(([slug, demo]) => {
            const Component = demo.component;
            return (
              <StaggerItem key={slug} className="scroll-mt-28 sm:scroll-mt-32" id={slug}>
                <Component />
              </StaggerItem>
            );
          })}
        </Stagger>

        <FadeUp>
          <div className="mt-10 sm:mt-16 rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 sm:p-7 text-center" data-testid="demos-footer-cta">
            <div className="text-white text-[14px] sm:text-[16px] font-semibold mb-1">Une démo moto manque ? Une idée à tester ?</div>
            <p className="text-white/60 text-[12px] sm:text-[13.5px] mb-4 max-w-xl mx-auto">
              Suggère-nous une démo spécifique moto (trajectoire en virage, prise d'angle, freinage d'urgence pied + main…) — on les ajoute régulièrement.
            </p>
            <a href="mailto:contact@code2roues.fr?subject=Suggestion%20de%20d%C3%A9mo%20interactive%20moto" className="inline-flex items-center gap-2 h-10 sm:h-11 px-4 sm:px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[12px] sm:text-[13.5px] font-black uppercase tracking-widest">
              Suggérer une démo <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </FadeUp>

        <FadeUp>
          <div className="mt-6 sm:mt-8 text-center text-[10px] sm:text-[10.5px] text-white/35 font-mono-spec leading-relaxed">
            Démos calibrées ECSR · Sources : Mariotte (1660), Dinges &amp; Powell (PVT 1985),<br className="hidden sm:block" />
            Cossalter « Motorcycle Dynamics » (2006), Sécurité Routière, littérature scientifique vision/alcool.
          </div>
        </FadeUp>
      </section>
    </div>
  );
}
